import { useState, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTheme } from '@mui/material'
import { toast } from 'react-hot-toast'
import { sshKeysAPI } from '../services/api'
import type {
  SSHConnectionDiagnosticsRequest,
  SSHConnectionDiagnosticsResponse,
  SSHHostKeyResponse,
} from '../services/api'
import { getApiErrorDetail } from '../utils/apiErrors'
import { translateBackendKey } from '../utils/translateBackendKey'
import { useAnalytics } from '../hooks/useAnalytics'
import { useAuth } from '../hooks/useAuth'
import { SSHConnectionsSingleKeyView } from './ssh-connections-single-key/SSHConnectionsSingleKeyView'
import { HostKeyDialog } from './ssh-connections-single-key/dialogs/HostKeyDialog'
import {
  createConnectionForm,
  createEditConnectionForm,
  createImportForm,
  createTestConnectionForm,
} from './ssh-connections-single-key/formDefaults'
import { normalizeSshHostInput } from './ssh-connections-single-key/hostValidation'
import { ConnectionDiagnosticsDialog } from './ssh-connections-single-key/dialogs/ConnectionDiagnosticsDialog'
import type {
  DeployConnectionPayload,
  ImportKeyPayload,
  SSHConnection,
  TestConnectionPayload,
  UpdateConnectionPayload,
} from './ssh-connections-single-key/types'

export default function SSHConnectionsSingleKey() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { track, EventCategory, EventAction } = useAnalytics()
  const { hasGlobalPermission } = useAuth()
  const canManageSsh = hasGlobalPermission('settings.ssh.manage')
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // State
  const [keyVisible, setKeyVisible] = useState(false)
  const [fingerprintVisible, setFingerprintVisible] = useState(false)
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [deployDialogOpen, setDeployDialogOpen] = useState(false)
  const [testConnectionDialogOpen, setTestConnectionDialogOpen] = useState(false)
  const [editConnectionDialogOpen, setEditConnectionDialogOpen] = useState(false)
  const [deleteConnectionDialogOpen, setDeleteConnectionDialogOpen] = useState(false)
  const [deleteKeyDialogOpen, setDeleteKeyDialogOpen] = useState(false)
  const [redeployKeyDialogOpen, setRedeployKeyDialogOpen] = useState(false)
  const [diagnosticsDialogOpen, setDiagnosticsDialogOpen] = useState(false)
  const [selectedConnection, setSelectedConnection] = useState<SSHConnection | null>(null)
  const [diagnosticsConnection, setDiagnosticsConnection] = useState<SSHConnection | null>(null)
  const [hostKeyDialogOpen, setHostKeyDialogOpen] = useState(false)
  const [hostKeyConnection, setHostKeyConnection] = useState<SSHConnection | null>(null)
  const [hostKey, setHostKey] = useState<SSHHostKeyResponse | null>(null)
  const [hostKeyLoading, setHostKeyLoading] = useState(false)
  const hostKeyRequestRef = useRef<number | null>(null)
  const [diagnosticsResult, setDiagnosticsResult] =
    useState<SSHConnectionDiagnosticsResponse | null>(null)
  const [keyType, setKeyType] = useState('ed25519')
  const [redeployPassword, setRedeployPassword] = useState('')
  const [importForm, setImportForm] = useState(createImportForm)
  const [connectionForm, setConnectionForm] = useState(createConnectionForm)
  const [testConnectionForm, setTestConnectionForm] = useState(createTestConnectionForm)
  const [editConnectionForm, setEditConnectionForm] = useState(createEditConnectionForm)
  const [connectionHostError, setConnectionHostError] = useState<string>()
  const [testConnectionHostError, setTestConnectionHostError] = useState<string>()
  const [editConnectionHostError, setEditConnectionHostError] = useState<string>()

  // Queries
  const { data: systemKeyData, isLoading: keyLoading } = useQuery({
    queryKey: ['system-ssh-key'],
    queryFn: sshKeysAPI.getSystemKey,
    enabled: canManageSsh,
    refetchInterval: 30000,
  })

  const { data: connectionsData, isLoading: connectionsLoading } = useQuery({
    queryKey: ['ssh-connections'],
    queryFn: sshKeysAPI.getSSHConnections,
    enabled: canManageSsh,
    refetchInterval: 30000,
  })

  const systemKey = systemKeyData?.data?.ssh_key
  const keyExists = systemKeyData?.data?.exists
  const connections: SSHConnection[] = connectionsData?.data?.connections || []

  // Mutations
  const generateKeyMutation = useMutation({
    mutationFn: (data: { name: string; key_type: string; description?: string }) =>
      sshKeysAPI.generateSSHKey(data),
    onSuccess: () => {
      toast.success(t('sshConnections.toasts.keyGenerated'))
      queryClient.invalidateQueries({ queryKey: ['system-ssh-key'] })
      setGenerateDialogOpen(false)
      track(EventCategory.SSH, EventAction.CREATE, { resource: 'key' })
    },
    onError: (error: unknown) => {
      console.error('Failed to generate SSH key:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.keyGenerateFailed')
      )
    },
  })

  const importKeyMutation = useMutation({
    mutationFn: (data: ImportKeyPayload) => sshKeysAPI.importSSHKey(data),
    onSuccess: () => {
      toast.success(t('sshConnections.toasts.keyImported'))
      queryClient.invalidateQueries({ queryKey: ['system-ssh-key'] })
      setImportDialogOpen(false)
      setImportForm(createImportForm())
      track(EventCategory.SSH, EventAction.UPLOAD, { resource: 'key' })
    },
    onError: (error: unknown) => {
      console.error('Failed to import SSH key:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) || t('sshConnections.toasts.keyImportFailed')
      )
    },
  })

  const deployKeyMutation = useMutation({
    mutationFn: (data: { keyId: number; connectionData: DeployConnectionPayload }) =>
      sshKeysAPI.deploySSHKey(data.keyId, data.connectionData),
    onSuccess: () => {
      toast.success(t('sshConnections.toasts.keyDeployed'))
      queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
      setDeployDialogOpen(false)
      setConnectionForm(createConnectionForm())
      track(EventCategory.SSH, EventAction.CREATE, { resource: 'connection' })
    },
    onError: (error: unknown) => {
      console.error('Failed to deploy SSH key:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) || t('sshConnections.toasts.keyDeployFailed')
      )
    },
  })

  const testConnectionMutation = useMutation({
    mutationFn: (data: { keyId: number; connectionData: TestConnectionPayload }) =>
      sshKeysAPI.testSSHConnection(data.keyId, data.connectionData),
    onSuccess: (response) => {
      if (response.data.success) {
        toast.success(
          translateBackendKey(response.data.message, 'sshConnections.toasts.connectionTestSuccess')
        )
        track(EventCategory.SSH, EventAction.TEST, { resource: 'connection' })
      } else {
        toast.error(t('sshConnections.toasts.connectionTestFailed'))
      }
      queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
    },
    onError: (error: unknown) => {
      console.error('Failed to test connection:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.connectionTestFailed')
      )
    },
  })

  const updateConnectionMutation = useMutation({
    mutationFn: (data: { connectionId: number; connectionData: UpdateConnectionPayload }) =>
      sshKeysAPI.updateSSHConnection(data.connectionId, data.connectionData),
    onSuccess: async (_response, variables) => {
      toast.success(t('sshConnections.toasts.connectionUpdated'))
      setEditConnectionDialogOpen(false)
      setSelectedConnection(null)
      track(EventCategory.SSH, EventAction.EDIT, { resource: 'connection' })

      // Automatically test the connection after update
      try {
        await sshKeysAPI.testExistingConnection(variables.connectionId)
        queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
      } catch (error: unknown) {
        // Test failure is already shown in the connection status
        console.error('Failed to test connection:', error)
        queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
      }
    },
    onError: (error: unknown) => {
      console.error('Failed to update connection:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.connectionUpdateFailed')
      )
    },
  })

  const deleteConnectionMutation = useMutation({
    mutationFn: (connectionId: number) => sshKeysAPI.deleteSSHConnection(connectionId),
    onSuccess: () => {
      toast.success(t('sshConnections.toasts.connectionDeleted'))
      queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
      setDeleteConnectionDialogOpen(false)
      setSelectedConnection(null)
      track(EventCategory.SSH, EventAction.DELETE, { resource: 'connection' })
    },
    onError: (error: unknown) => {
      console.error('Failed to delete connection:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.connectionDeleteFailed')
      )
    },
  })

  const refreshStorageMutation = useMutation({
    mutationFn: (connectionId: number) => sshKeysAPI.refreshConnectionStorage(connectionId),
    onSuccess: (response) => {
      // The backend answers 200 with success=false when the remote shell
      // refuses `df` (restricted key); that is not an error, just no data.
      if (response.data.success) {
        toast.success(t('sshConnections.toasts.storageRefreshed'))
      } else {
        toast(
          translateBackendKey(response.data.message, 'sshConnections.toasts.storageRefreshFailed')
        )
      }
      queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
      track(EventCategory.SSH, EventAction.VIEW, { resource: 'storage' })
    },
    onError: (error: unknown) => {
      console.error('Failed to refresh storage:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.storageRefreshFailed')
      )
    },
  })

  const testExistingConnectionMutation = useMutation({
    mutationFn: (connectionId: number) => sshKeysAPI.testExistingConnection(connectionId),
    onSuccess: (response) => {
      if (response.data.success) {
        toast.success(
          translateBackendKey(response.data.message, 'sshConnections.toasts.connectionTestSuccess')
        )
      } else {
        toast.error(
          translateBackendKey(response.data.error) ||
            t('sshConnections.toasts.connectionTestFailed')
        )
      }
      queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
      track(EventCategory.SSH, EventAction.TEST, { resource: 'connection' })
    },
    onError: (error: unknown) => {
      console.error('Failed to test connection:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.connectionTestFailed')
      )
    },
  })

  const trustHostKeyMutation = useMutation({
    mutationFn: ({ connectionId, key }: { connectionId: number; key: string }) =>
      sshKeysAPI.trustConnectionHostKey(connectionId, key),
    onSuccess: (response, variables) => {
      toast.success(t('sshConnections.toasts.hostKeyTrusted'))
      if (hostKeyRequestRef.current === variables.connectionId) {
        setHostKey(response.data)
      }
      queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
    },
    onError: (error: unknown) => {
      console.error('Failed to trust host key:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.hostKeyTrustFailed')
      )
    },
  })

  const forgetHostKeyMutation = useMutation({
    mutationFn: (connectionId: number) => sshKeysAPI.forgetConnectionHostKey(connectionId),
    onSuccess: () => {
      toast.success(t('sshConnections.toasts.hostKeyForgotten'))
      setHostKeyDialogOpen(false)
      setHostKeyConnection(null)
      setHostKey(null)
      queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
    },
    onError: (error: unknown) => {
      console.error('Failed to forget host key:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.hostKeyForgetFailed')
      )
    },
  })

  const deleteKeyMutation = useMutation({
    mutationFn: (keyId: number) => sshKeysAPI.deleteSSHKey(keyId),
    onSuccess: () => {
      toast.success(t('sshConnections.toasts.keyDeleted'))
      queryClient.invalidateQueries({ queryKey: ['system-ssh-key'] })
      queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
      setDeleteKeyDialogOpen(false)
      track(EventCategory.SSH, EventAction.DELETE, { resource: 'key' })
    },
    onError: (error: unknown) => {
      console.error('Failed to delete SSH key:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) || t('sshConnections.toasts.keyDeleteFailed')
      )
    },
  })

  const redeployKeyMutation = useMutation({
    mutationFn: ({ connectionId, password }: { connectionId: number; password: string }) =>
      sshKeysAPI.redeployKeyToConnection(connectionId, password),
    onSuccess: (response) => {
      if (response.data.success) {
        toast.success(t('sshConnections.toasts.keyDeployed'))
        queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
        setRedeployKeyDialogOpen(false)
        setRedeployPassword('')
        track(EventCategory.SSH, EventAction.START, {
          resource: 'connection',
          operation: 'deploy_key',
        })
      } else {
        toast.error(
          translateBackendKey(response.data.error) || t('sshConnections.toasts.keyDeployFailed')
        )
      }
    },
    onError: (error: unknown) => {
      console.error('Failed to redeploy SSH key:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) || t('sshConnections.toasts.keyDeployFailed')
      )
    },
  })

  const runConnectionDiagnosticsMutation = useMutation({
    mutationFn: ({
      connectionId,
      payload,
    }: {
      connectionId: number
      payload: SSHConnectionDiagnosticsRequest
    }) => sshKeysAPI.runConnectionDiagnostics(connectionId, payload),
    onSuccess: (response) => {
      setDiagnosticsResult(response.data)
      toast.success(t('sshConnections.toasts.diagnosticsComplete'))
      track(EventCategory.SSH, EventAction.TEST, { resource: 'connection_diagnostics' })
    },
    onError: (error: unknown) => {
      console.error('Failed to run connection diagnostics:', error)
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.diagnosticsFailed')
      )
    },
  })

  // Auto-refresh storage for connections without storage info
  useEffect(() => {
    if (!canManageSsh) {
      return
    }
    if (connections && connections.length > 0) {
      const connectionsWithoutStorage = connections.filter((conn) => !conn.storage)

      if (connectionsWithoutStorage.length > 0) {
        // Refresh storage for each connection without storage (silently)
        connectionsWithoutStorage.forEach((conn) => {
          sshKeysAPI.refreshConnectionStorage(conn.id).catch(() => {
            // Silently fail - will show "No storage info" in card
          })
        })

        // Invalidate query after delay to show updated data
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
        }, 2000)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections?.length])

  if (!canManageSsh) {
    return <Navigate to="/dashboard" replace />
  }

  // Handlers
  const handleGenerateKey = () => {
    generateKeyMutation.mutate({
      name: t('sshConnections.systemKey.title'),
      key_type: keyType,
      description: t('sshConnections.systemKey.defaultDescription'),
    })
  }

  const handleImportKey = () => {
    importKeyMutation.mutate(importForm)
  }

  const handleCopyPublicKey = () => {
    if (systemKey?.public_key) {
      navigator.clipboard.writeText(systemKey.public_key)
      toast.success(t('sshConnections.toasts.publicKeyCopied'))
    }
  }

  const handleDeployKey = () => {
    if (!systemKey) return
    const normalizedHost = normalizeSshHostInput(connectionForm.host)
    if (!normalizedHost.ok) {
      setConnectionHostError(t(normalizedHost.errorKey))
      return
    }
    setConnectionHostError(undefined)
    deployKeyMutation.mutate({
      keyId: systemKey.id,
      connectionData: { ...connectionForm, host: normalizedHost.host },
    })
  }

  const handleTestManualConnection = () => {
    if (!systemKey) return
    const normalizedHost = normalizeSshHostInput(testConnectionForm.host)
    if (!normalizedHost.ok) {
      setTestConnectionHostError(t(normalizedHost.errorKey))
      return
    }
    setTestConnectionHostError(undefined)
    testConnectionMutation.mutate({
      keyId: systemKey.id,
      connectionData: { ...testConnectionForm, host: normalizedHost.host },
    })
    setTestConnectionDialogOpen(false)
    setTestConnectionForm(createTestConnectionForm())
  }

  const handleEditConnection = (connection: SSHConnection) => {
    setSelectedConnection(connection)
    setEditConnectionHostError(undefined)
    setEditConnectionForm({
      host: connection.host,
      username: connection.username,
      port: connection.port,
      use_sftp_mode: connection.use_sftp_mode,
      use_sudo: connection.use_sudo,
      default_path: connection.default_path || '',
      ssh_path_prefix: connection.ssh_path_prefix || '',
      mount_point: connection.mount_point || '',
    })
    setEditConnectionDialogOpen(true)
  }

  const handleUpdateConnection = () => {
    if (!selectedConnection) return
    const normalizedHost = normalizeSshHostInput(editConnectionForm.host)
    if (!normalizedHost.ok) {
      setEditConnectionHostError(t(normalizedHost.errorKey))
      return
    }
    setEditConnectionHostError(undefined)
    updateConnectionMutation.mutate({
      connectionId: selectedConnection.id,
      connectionData: { ...editConnectionForm, host: normalizedHost.host },
    })
  }

  const handleDeleteConnection = (connection: SSHConnection) => {
    setSelectedConnection(connection)
    setDeleteConnectionDialogOpen(true)
  }

  const confirmDeleteConnection = () => {
    if (!selectedConnection) return
    deleteConnectionMutation.mutate(selectedConnection.id)
  }

  const handleTestConnection = (connection: SSHConnection) => {
    testExistingConnectionMutation.mutate(connection.id)
  }

  const handleVerifyHostKey = async (connection: SSHConnection) => {
    // Scanning a host takes seconds, so the user can close this dialog or open
    // another machine's before the answer arrives. Every update below is guarded
    // on the dialog still showing the connection that was asked about, otherwise
    // one machine's fingerprint could appear under another machine's name.
    hostKeyRequestRef.current = connection.id
    setHostKeyConnection(connection)
    setHostKey(null)
    setHostKeyDialogOpen(true)
    setHostKeyLoading(true)
    try {
      const response = await sshKeysAPI.getConnectionHostKey(connection.id)
      if (hostKeyRequestRef.current !== connection.id) return
      setHostKey(response.data)
    } catch (error) {
      console.error('Failed to read host key:', error)
      if (hostKeyRequestRef.current !== connection.id) return
      toast.error(
        translateBackendKey(getApiErrorDetail(error)) ||
          t('sshConnections.toasts.hostKeyReadFailed')
      )
      setHostKeyDialogOpen(false)
    } finally {
      if (hostKeyRequestRef.current === connection.id) {
        setHostKeyLoading(false)
      }
    }
  }

  const closeHostKeyDialog = () => {
    hostKeyRequestRef.current = null
    setHostKeyDialogOpen(false)
    setHostKeyConnection(null)
    setHostKey(null)
  }

  const handleTrustHostKey = () => {
    if (!hostKeyConnection || !hostKey?.observed_key) return
    trustHostKeyMutation.mutate({
      connectionId: hostKeyConnection.id,
      key: hostKey.observed_key,
    })
  }

  const handleForgetHostKey = () => {
    if (!hostKeyConnection) return
    forgetHostKeyMutation.mutate(hostKeyConnection.id)
  }

  const handleRunDiagnostics = (connection: SSHConnection) => {
    setDiagnosticsConnection(connection)
    setDiagnosticsResult(null)
    setDiagnosticsDialogOpen(true)
  }

  const handleRunConnectionDiagnostics = async (
    connection: SSHConnection,
    payload: SSHConnectionDiagnosticsRequest
  ) => {
    const response = await runConnectionDiagnosticsMutation.mutateAsync({
      connectionId: connection.id,
      payload,
    })
    return response.data
  }

  const handleDeployKeyToConnection = (connection: SSHConnection) => {
    setSelectedConnection(connection)
    setRedeployKeyDialogOpen(true)
  }

  const handleConfirmRedeployKey = () => {
    if (!selectedConnection || !redeployPassword) return
    redeployKeyMutation.mutate({
      connectionId: selectedConnection.id,
      password: redeployPassword,
    })
  }

  const handleDeleteKey = () => {
    if (!systemKey) return
    deleteKeyMutation.mutate(systemKey.id)
  }

  return (
    <>
      <SSHConnectionsSingleKeyView
        t={t}
        theme={theme}
        isDark={isDark}
        keyLoading={keyLoading}
        connectionsLoading={connectionsLoading}
        keyExists={keyExists}
        systemKey={systemKey}
        connections={connections}
        canManageSsh={canManageSsh}
        keyVisible={keyVisible}
        setKeyVisible={setKeyVisible}
        fingerprintVisible={fingerprintVisible}
        setFingerprintVisible={setFingerprintVisible}
        generateDialogOpen={generateDialogOpen}
        setGenerateDialogOpen={setGenerateDialogOpen}
        importDialogOpen={importDialogOpen}
        setImportDialogOpen={setImportDialogOpen}
        deployDialogOpen={deployDialogOpen}
        setDeployDialogOpen={setDeployDialogOpen}
        testConnectionDialogOpen={testConnectionDialogOpen}
        setTestConnectionDialogOpen={setTestConnectionDialogOpen}
        editConnectionDialogOpen={editConnectionDialogOpen}
        setEditConnectionDialogOpen={setEditConnectionDialogOpen}
        deleteConnectionDialogOpen={deleteConnectionDialogOpen}
        setDeleteConnectionDialogOpen={setDeleteConnectionDialogOpen}
        deleteKeyDialogOpen={deleteKeyDialogOpen}
        setDeleteKeyDialogOpen={setDeleteKeyDialogOpen}
        redeployKeyDialogOpen={redeployKeyDialogOpen}
        setRedeployKeyDialogOpen={setRedeployKeyDialogOpen}
        selectedConnection={selectedConnection}
        setSelectedConnection={setSelectedConnection}
        keyType={keyType}
        setKeyType={setKeyType}
        redeployPassword={redeployPassword}
        setRedeployPassword={setRedeployPassword}
        importForm={importForm}
        setImportForm={setImportForm}
        connectionForm={connectionForm}
        setConnectionForm={setConnectionForm}
        connectionHostError={connectionHostError}
        setConnectionHostError={setConnectionHostError}
        testConnectionForm={testConnectionForm}
        setTestConnectionForm={setTestConnectionForm}
        testConnectionHostError={testConnectionHostError}
        setTestConnectionHostError={setTestConnectionHostError}
        editConnectionForm={editConnectionForm}
        setEditConnectionForm={setEditConnectionForm}
        editConnectionHostError={editConnectionHostError}
        setEditConnectionHostError={setEditConnectionHostError}
        generateKeyPending={generateKeyMutation.isPending}
        importKeyPending={importKeyMutation.isPending}
        deployKeyPending={deployKeyMutation.isPending}
        testConnectionPending={testConnectionMutation.isPending}
        updateConnectionPending={updateConnectionMutation.isPending}
        deleteConnectionPending={deleteConnectionMutation.isPending}
        deleteKeyPending={deleteKeyMutation.isPending}
        redeployKeyPending={redeployKeyMutation.isPending}
        handleGenerateKey={handleGenerateKey}
        handleImportKey={handleImportKey}
        handleCopyPublicKey={handleCopyPublicKey}
        handleDeployKey={handleDeployKey}
        handleTestManualConnection={handleTestManualConnection}
        handleEditConnection={handleEditConnection}
        handleUpdateConnection={handleUpdateConnection}
        handleDeleteConnection={handleDeleteConnection}
        confirmDeleteConnection={confirmDeleteConnection}
        handleTestConnection={handleTestConnection}
        handleDeployKeyToConnection={handleDeployKeyToConnection}
        handleRunDiagnostics={handleRunDiagnostics}
        handleVerifyHostKey={handleVerifyHostKey}
        handleConfirmRedeployKey={handleConfirmRedeployKey}
        handleDeleteKey={handleDeleteKey}
        onRefreshConnections={() =>
          queryClient.invalidateQueries({ queryKey: ['ssh-connections'] })
        }
        onRefreshStorage={(connectionId) => refreshStorageMutation.mutate(connectionId)}
      />
      <HostKeyDialog
        t={t}
        open={hostKeyDialogOpen}
        onClose={closeHostKeyDialog}
        connection={hostKeyConnection}
        hostKey={hostKey}
        loading={hostKeyLoading}
        pending={trustHostKeyMutation.isPending || forgetHostKeyMutation.isPending}
        onTrust={handleTrustHostKey}
        onForget={handleForgetHostKey}
      />
      <ConnectionDiagnosticsDialog
        open={diagnosticsDialogOpen}
        connection={diagnosticsConnection}
        initialResult={diagnosticsResult}
        onClose={() => setDiagnosticsDialogOpen(false)}
        onRunDiagnostics={handleRunConnectionDiagnostics}
      />
    </>
  )
}
